import axios from 'axios';
import signature from '@/services/signature';

const BASE_URI = process.env.VUE_APP_API_BASE_URI;

/*
  Customer-facing QR Service endpoints — signature only.

  These endpoints DO NOT require a Bearer token. Auth is the HMAC signature
  (key + timestamp + signature appended via signature()) plus the session
  token in the URL — the customer's identity is just "whoever holds this
  scannable URL". So we intentionally omit Authorization here.
*/
const config = {
  headers: {
    TimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
  }
};

class ServiceMenuApi {
  /**
   * GET /v1/services/{token}/menu
   * Returns every menu item available to this session's branch.
   * Filter knobs (all optional): product_category_id, search, sort.
   */
  async list(token, queryString = '') {
    const response = await axios.get(
      BASE_URI + '/v1/services/' + token + '/menu' + signature(queryString),
      config
    );
    return response;
  }

  /**
   * GET /v1/services/{token}/menu/{productId}
   * Returns the full product detail with attributes/variants/modifiers
   * for the customisation sheet.
   */
  async info(token, productId) {
    const response = await axios.get(
      BASE_URI + '/v1/services/' + token + '/menu/' + productId + signature(),
      config
    );
    return response;
  }
}

class ServiceRequestsApi {
  /**
   * GET /v1/services/{token}/requests
   * Returns the recent service requests for this session, newest first,
   * plus `filter.types` — the catalogue of "call staff" + quick-request
   * buttons configured for this branch (already ordered by priority asc;
   * split client-side by `is_call_staff` for the two UI sections).
   * Optional filters: state, limit (1..500, default 50).
   */
  async list(token, queryString = '') {
    const response = await axios.get(
      BASE_URI + '/v1/services/' + token + '/requests' + signature(queryString),
      config
    );
    return response;
  }

  /**
   * POST /v1/services/{token}/requests
   * Body: { service_request_type_id, note? }
   * `note` is REQUIRED when the chosen type has `requires_note=true`,
   * otherwise the API returns 422.
   */
  async create(token, data) {
    const response = await axios.post(
      BASE_URI + '/v1/services/' + token + '/requests' + signature(),
      data,
      config
    );
    return response;
  }
}

class ServiceOrdersApi {
  /**
   * POST /v1/services/{token}/orders
   * Body: { customer_count?, items: [{ product_id, quantity, remark?, modifier_option_ids? }] }
   *
   * Notes for callers:
   *   • Do NOT send any other keys — the API enforces NotAllowedRule and
   *     will 422 on extras (no `type`, `discount`, `delivery_platform_id`,
   *     `customer_id`, `external_order_ref`). type is forced to "dine_in".
   *   • For configurable products, `product_id` must be the **variant** id
   *     (the variant row IS a product). Use cartItem.variant_id || cartItem.product_id.
   *   • The server validates modifier groups against is_required, min/max,
   *     and allow_multiple — error message comes back in 422.message,
   *     surface it to the customer as-is.
   *
   * On success, re-fetch /my-order to render the new queued items.
   */
  async create(token, data) {
    const response = await axios.post(
      BASE_URI + '/v1/services/' + token + '/orders' + signature(),
      data,
      config
    );
    return response;
  }
}

class ServiceBillsApi {
  /**
   * GET /v1/services/{token}/bills
   * Returns every bill ever opened in this session (id desc), plus the
   * `session` block with `is_bill_requested` / `bill_requested_at`.
   *
   * Notes:
   *   • One session may carry multiple bills (e.g. first bill paid,
   *     customer ordered again → new bill opened).
   *   • All decimal fields ("sub_total", "discount", "service_charge_amount",
   *     `items[].price`, etc.) arrive as strings — parse to number before
   *     arithmetic.
   *   • `closed_at === ""` means the bill is still open and can be added to.
   *     Use the `payment_status` field to drive the badge.
   *   • No query params are allowed — sending extras 422s.
   */
  async list(token) {
    const response = await axios.get(
      BASE_URI + '/v1/services/' + token + '/bills' + signature(),
      config
    );
    return response;
  }

  /**
   * PUT /v1/services/{token}/bills/request
   * Customer-side "I'd like the bill" signal. Server stamps `now()` onto
   * session.bill_requested_at and returns the updated session block:
   *   { id, token, state, bill_requested_at, is_bill_requested }
   *
   * Important:
   *   • NO body, NO query params — sending either 422s (NotAllowedRule).
   *   • Idempotent for UI state but the timestamp is overwritten on every
   *     call → safe to use this same method for the "Request again" tap
   *     after a cooldown.
   *   • There is no clear-bill-request endpoint — only POS can reset it.
   */
  async request(token) {
    const response = await axios.put(
      BASE_URI + '/v1/services/' + token + '/bills/request' + signature(),
      {},
      config
    );
    return response;
  }
}

class ServiceMyOrderApi {
  /**
   * GET /v1/services/{token}/my-order
   * Returns the table info + every order item placed in this session so
   * far. Decimal fields come back as strings ("25.00") — parse before
   * doing arithmetic on them. No query params allowed.
   */
  async get(token) {
    const response = await axios.get(
      BASE_URI + '/v1/services/' + token + '/my-order' + signature(),
      config
    );
    return response;
  }
}

class ServicePromotionApi {
  /**
   * GET /v1/services/{token}/promotion
   * โปรซื้อครบของบิลในเซสชันนี้ (นิยาม tier + สถานะจาก item ที่ส่งครัวแล้ว)
   * FE เอา tier ไปคำนวณ progress รวมตะกร้า (ยังไม่ส่ง) เพิ่มเอง
   */
  async get(token) {
    const response = await axios.get(
      BASE_URI + '/v1/services/' + token + '/promotion' + signature(),
      config
    );
    return response;
  }

  /**
   * POST /v1/services/{token}/promotion/claim — รับสิทธิ์ tier ที่ถึงแล้ว ({ promotion_item_id })
   * ต้องส่งครัวจนยอดถึง threshold ก่อน (BE ตรวจซ้ำ)
   */
  async claim(token, data) {
    const response = await axios.post(
      BASE_URI + '/v1/services/' + token + '/promotion/claim' + signature(),
      data,
      config
    );
    return response;
  }
}

class ServiceLoyaltyApi {
  /**
   * GET /v1/services/{token}/loyalty
   * แต้มสะสม (read-only) ของสมาชิกที่ผูกกับโต๊ะ/เซสชันนี้ + แต้มที่จะได้รับ
   * ไม่มีการแลกฝั่งลูกค้า (staff เท่านั้น) — ใช้แสดงผลอย่างเดียว
   */
  async get(token) {
    const response = await axios.get(
      BASE_URI + '/v1/services/' + token + '/loyalty' + signature(),
      config
    );
    return response;
  }
}

/**
 * True when an Axios error is the API's "session is dead" signal:
 *   422 { code: "VALIDATION", message: "service session invalid!" }
 *
 * The session token in the QR URL can expire / be closed by staff while a
 * customer still has the page open (or be typed in wrong by someone poking
 * at the URL). Every service tab routes such errors here so the shell can
 * bounce the customer to the public menu preview instead of showing a
 * broken session.
 *
 * NOTE: matched on code + a "session" keyword in the message, NOT on 422
 * alone — POST /orders, /requests etc. also return 422 (code VALIDATION)
 * for ordinary field validation, and those must NOT trigger a redirect.
 */
export function isServiceSessionInvalid(err) {
  const res = err?.response;
  if (!res || res.status !== 422) return false;
  const data = res.data || {};
  const message = String(data.message || '').toLowerCase();
  return data.code === 'VALIDATION' && message.includes('session');
}

export const ServiceMenu = new ServiceMenuApi();
export const ServiceMyOrder = new ServiceMyOrderApi();
export const ServiceRequests = new ServiceRequestsApi();
export const ServiceOrders = new ServiceOrdersApi();
export const ServiceBills = new ServiceBillsApi();
export const ServiceLoyalty = new ServiceLoyaltyApi();
export const ServicePromotion = new ServicePromotionApi();

